import { Component } from '@theme/component';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from '@theme/events';

const SLIDESHOW_SELECT_EVENT = 'slideshow:select';
const VARIANT_LOADING_FAILSAFE = 8000;

export class MediaGallery extends Component {
  #controller = new AbortController();
  #variantLoading = false;
  #loadingFailsafeTimer = null;
  #mediaSyncFrame = null;
  #slideObserver = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener('change', this.#handleVariantChangeStart, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.addEventListener(SLIDESHOW_SELECT_EVENT, this.#handleSlideshowSelection, { signal });

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    // KONHILL: capture 阶段拦截缩略图点击
    this.addEventListener('click', this.#handleThumbnailClick, { signal, capture: true });

    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.#setupSlideObserver();
      this.#scheduleActiveMediaSync();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#clearLoadingFailsafe();

    if (this.#mediaSyncFrame !== null) {
      cancelAnimationFrame(this.#mediaSyncFrame);
      this.#mediaSyncFrame = null;
    }

    this.#slideObserver?.disconnect();
    this.#slideObserver = null;
    this.#pauseAllGalleryMedia();
  }

  // KONHILL: 用 slideshow.slides（过滤 hidden 后的子集）找 index，与 select() 保持一致
  #handleThumbnailClick = (event) => {
    const btn = event.target.closest('[data-media-id]');
    if (!btn) return;

    event.stopImmediatePropagation();
    event.preventDefault();

    const mediaId = String(btn.dataset.mediaId);
    const slideshow = this.slideshow;
    if (!slideshow) return;

    const slides = slideshow.slides;
    if (!slides) return;

    const index = slides.findIndex(
      (slide) => String(slide.getAttribute('slide-id')) === mediaId
    );

    if (index !== -1) {
      slideshow.select(index);
    }
  };

  #handleVariantChangeStart = (event) => {
    if (!(event.target instanceof Element)) return;
    const variantPicker = event.target.closest('variant-picker');
    if (!variantPicker) return;
    if (variantPicker.closest('product-card, quick-add-dialog')) return;
    this.#startVariantLoading();
  };

  #startVariantLoading() {
    this.#variantLoading = true;
    this.setAttribute('data-variant-loading', '');
    this.setAttribute('aria-busy', 'true');

    const slideshow = this.slideshow;
    if (slideshow) {
      slideshow.style.pointerEvents = 'none';
      slideshow.style.userSelect = 'none';
      slideshow.setAttribute('aria-busy', 'true');
    }

    this.#clearLoadingFailsafe();
    this.#loadingFailsafeTimer = window.setTimeout(() => {
      if (this.isConnected) this.#finishVariantLoading();
    }, VARIANT_LOADING_FAILSAFE);
  }

  #finishVariantLoading() {
    this.#variantLoading = false;
    this.removeAttribute('data-variant-loading');
    this.removeAttribute('aria-busy');

    const slideshow = this.slideshow;
    if (slideshow) {
      slideshow.style.removeProperty('pointer-events');
      slideshow.style.removeProperty('user-select');
      slideshow.removeAttribute('aria-busy');
    }

    this.#clearLoadingFailsafe();
  }

  #clearLoadingFailsafe() {
    if (this.#loadingFailsafeTimer === null) return;
    clearTimeout(this.#loadingFailsafeTimer);
    this.#loadingFailsafeTimer = null;
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) { this.#finishVariantLoading(); return; }

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) { this.#finishVariantLoading(); return; }
    if (!this.isConnected) return;

    // 同一主图的变体更新（例如只更改尺码）应保留当前浏览的媒体。
    // 使用 slide-id 而非索引：新的 Liquid 可能重新排序或隐藏部分媒体。
    const oldSlideshow = this.slideshow;
    const oldSlides = Array.from(oldSlideshow?.slides ?? []);
    const activeSlide =
      oldSlides[oldSlideshow?.current ?? 0] ??
      oldSlideshow?.querySelector('slideshow-slide[aria-hidden="false"]:not([hidden])');
    const activeMediaId = activeSlide?.getAttribute('slide-id');
    const oldFirstMediaId = oldSlides[0]?.getAttribute('slide-id');
    const newFirstMediaId = newMediaGallery
      .querySelector('slideshow-component slideshow-slide:not([hidden])')
      ?.getAttribute('slide-id');
    const preserveMedia = Boolean(
      activeMediaId && oldFirstMediaId && oldFirstMediaId === newFirstMediaId
    );

    this.#pauseAllGalleryMedia();
    this.replaceWith(newMediaGallery);

    if (preserveMedia) {
      requestAnimationFrame(() => {
        if (!newMediaGallery.isConnected) return;
        const newSlideshow = newMediaGallery.slideshow;
        const index = Array.from(newSlideshow?.slides ?? []).findIndex(
          (slide) => slide.getAttribute('slide-id') === activeMediaId
        );
        if (index !== -1) {
          newSlideshow.select(index, undefined, { animate: false });
        }
      });
    }
  };

  #handleSlideshowSelection = (event) => {
    if (event.target !== this.slideshow) return;
    this.#scheduleActiveMediaSync();
  };

  #setupSlideObserver() {
    this.#slideObserver?.disconnect();
    this.#slideObserver = null;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    this.#slideObserver = new MutationObserver((mutations) => {
      const activeSlideChanged = mutations.some(
        (m) => m.type === 'attributes' && m.attributeName === 'aria-hidden'
      );
      if (activeSlideChanged) this.#scheduleActiveMediaSync();
    });

    this.#slideObserver.observe(slideshow, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  #scheduleActiveMediaSync() {
    if (this.#mediaSyncFrame !== null) {
      cancelAnimationFrame(this.#mediaSyncFrame);
    }
    this.#mediaSyncFrame = requestAnimationFrame(() => {
      this.#mediaSyncFrame = null;
      if (!this.isConnected) return;
      this.#syncActiveSlideMedia();
    });
  }

  #syncActiveSlideMedia() {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    if (
      this.presentation === 'grid' &&
      window.matchMedia('(min-width: 750px)').matches
    ) {
      this.#pauseAllGalleryMedia();
      return;
    }

    const slides = Array.from(
      slideshow.querySelectorAll('slideshow-slide')
    ).filter((slide) => !slide.hasAttribute('hidden'));

    if (slides.length === 0) return;

    const currentIndex = slideshow.current ?? 0;
    const activeSlide =
      slides[currentIndex] ??
      slideshow.querySelector('slideshow-slide[aria-hidden="false"]');

    for (const slide of slides) {
      const isActive = slide === activeSlide;

      slide.querySelectorAll('deferred-media').forEach((deferredMedia) => {
        if (isActive && this.#isPlayableVideoSlide(slide, deferredMedia)) {
          this.#playDeferredMedia(deferredMedia);
        } else {
          this.#pauseDeferredMedia(deferredMedia);
        }
      });

      slide.querySelectorAll('video').forEach((video) => {
        if (video.closest('deferred-media')) return;
        if (isActive) {
          this.#playNativeVideo(video);
        } else {
          video.pause();
        }
      });
    }
  }

  #isPlayableVideoSlide(slide, deferredMedia) {
    if (deferredMedia.matches('product-model')) return false;
    return (
      slide.classList.contains('product-media-container--video') ||
      slide.classList.contains('product-media-container--external_video') ||
      Boolean(deferredMedia.querySelector('video, iframe[data-video-type], template'))
    );
  }

  #playDeferredMedia(deferredMedia) {
    try {
      if (!deferredMedia.hasAttribute('data-media-loaded')) {
        if (typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        } else if (typeof deferredMedia.showDeferredMedia === 'function') {
          deferredMedia.showDeferredMedia();
        }
      }

      const video = deferredMedia.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        this.#playNativeVideo(video);
        if ('isPlaying' in deferredMedia) deferredMedia.isPlaying = true;
        deferredMedia.updatePlayPauseHint?.(true);
        return;
      }

      if (typeof deferredMedia.playMedia === 'function') {
        deferredMedia.playMedia();
      }
    } catch (error) {
      console.warn('[media-gallery] Unable to autoplay product media:', error);
    }
  }

  #playNativeVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.play()?.catch?.(() => {});
  }

  #pauseDeferredMedia(deferredMedia) {
    try {
      if (typeof deferredMedia.pauseMedia === 'function') {
        deferredMedia.pauseMedia();
      } else {
        deferredMedia.querySelector('video')?.pause();
      }
    } catch (error) {
      console.warn('[media-gallery] Unable to pause product media:', error);
    }
  }

  #pauseAllGalleryMedia() {
    this.querySelectorAll('deferred-media').forEach((dm) => this.#pauseDeferredMedia(dm));
    this.querySelectorAll('video').forEach((v) => v.pause());
  }

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
    this.#scheduleActiveMediaSync();
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() { return this.refs.slideshow; }
  get media() { return this.refs.media; }
  get presentation() { return this.dataset.presentation; }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}